import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import axios from "axios";
import useSession from "./useSession";

/* ===== THEME (ერთ ადგილას ზომები) ===== */
const UI = {
  headerH: "64px",
  sidebarW: "250px",
  bg: "#0f1115",
  panel: "rgba(18,20,26,.72)",
  border: "rgba(255,255,255,.10)",
  ink: "#f8fafc",
  inkDim: "rgba(248,250,252,.75)",
  accent: "#facc15",
  accentSoft: "rgba(250,204,21,.14)",
  shadow: "0 12px 40px rgba(0,0,0,.28)",
  md: "14px",
  lg: "16px",
  bp: "992px",
};

/* ===== ზედა ჰედერი — ერთი ხაზი, მხოლოდ სახელი ===== */
const Topbar = styled.header`
  position: fixed; top: 0; left: 0; right: 0; height: ${UI.headerH};
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(14px, 3vw, 28px);
  background: ${UI.panel};
  backdrop-filter: blur(10px) saturate(120%);
  border-bottom: 1px solid ${UI.border};
  color: ${UI.ink};
  z-index: 1000;
`;

const Brand = styled(Link)`
  font-weight: 800; font-size: clamp(18px, 2.2vw, 22px);
  letter-spacing: .2px; color: ${UI.ink};
  text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  padding: 8px 10px; border-radius: 10px;
  transition: color .2s ease, background .2s ease;
  &:hover { color: ${UI.accent}; background: ${UI.accentSoft}; }
`;

const MobileToggle = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 12px;
  border: 1px solid ${UI.border};
  background: rgba(255,255,255,.06);
  color: ${UI.ink}; font-size: 22px; cursor: pointer;
  transition: background .2s ease;
  @media (min-width: ${UI.bp}) { display: none; }
  &:hover { background: rgba(255,255,255,.10); }
`;

/* ===== Sidebar — ფიქსირებული, დესკტოპზე ყოველთვის ჩანს ===== */
const Sidebar = styled.aside`
  position: fixed;
  top: ${UI.headerH}; bottom: 0; left: 0;
  width: ${UI.sidebarW};
  background: ${UI.bg};
  color: ${UI.ink};
  border-right: 1px solid ${UI.border};
  box-shadow: ${UI.shadow};
  overflow: auto;
  padding: 14px;
  z-index: 900; /* ჰედერზე დაბლა, კონტენტზე ზემოთ მხოლოდ მობილურზე */

  /* მობილურზე ხდება drawer */
  @media (max-width: ${UI.bp}) {
    transform: translateX(${({ $open }) => ($open ? "0%" : "-100%")});
    transition: transform .28s ease;
    z-index: 1100; /* overlay+drawer ზემოთ */
  }
`;

const GroupTitle = styled.div`
  font-size: ${UI.md}; font-weight: 700; opacity: .8;
  padding: 10px 8px; margin-bottom: 6px;
  border-bottom: 1px dashed ${UI.border};
`;

const Menu = styled.nav`
  display: grid; gap: 6px;
  margin-top: 8px;
`;

const Item = styled(Link)`
  display: block; text-decoration: none;
  color: ${UI.inkDim}; font-size: ${UI.md}; font-weight: 600;
  padding: 10px 12px; border-radius: 10px; border: 1px solid transparent;
  transition: background .18s ease, color .18s ease, border-color .18s ease;

  ${({ $active }) =>
    $active
      ? css`color:${UI.ink}; background:${UI.accentSoft}; border-color:${UI.border};`
      : css`&:hover{ color:${UI.ink}; background:rgba(255,255,255,.06); border-color:${UI.border}; }`}
`;

const DangerBtn = styled.button`
  margin-top: 10px; width: 100%;
  appearance: none; border: 1px solid rgba(239,68,68,.35);
  background: linear-gradient(180deg, rgba(239,68,68,.18), rgba(239,68,68,.12));
  color: #fff; font-size: ${UI.md}; font-weight: 700;
  padding: 10px 12px; border-radius: 10px; cursor: pointer;
  transition: filter .15s ease, transform .15s ease;
  &:hover { filter: brightness(1.05); }
  &:active { transform: translateY(1px); }
`;

/* ===== Overlay — მხოლოდ მობილურზე drawer-ისას ===== */
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity .25s ease;
  z-index: 1000;
  @media (min-width: ${UI.bp}) { display: none; }
`;

/* ===== Main — კონტენტი, რომელიც არასოდეს იფარება =====
   დესკტოპზე margin-left = sidebarW, რათა გვერდით გაიშალოს.
   თავზე padding-top = headerH, რადგან ჰედერი fixed-ია. */
export const Main = styled.main`
  min-height: 100vh;
  padding: calc(${UI.headerH} + 24px) 24px 24px 24px;
  margin-left: ${UI.sidebarW};

  /* მობილურზე sidebar overlay-ია, ამიტომ margin-left 0 */
  @media (max-width: ${UI.bp}) {
    margin-left: 0;
    padding-top: calc(${UI.headerH} + 16px);
  }
`;

export default function Navigation() {
  const { user, setUser } = useSession();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("user");
    axios
      .post("https://admin.shopaholics.ge/api/logout.php", {}, { withCredentials: true })
      .catch(() => {})
      .finally(() => window.location.replace("https://admin.shopaholics.ge/?logout=1"));
  };

  if (!user) return null;

  return (
    <>
      <Topbar>
        <Brand to="/">PerfumeShop</Brand>
        <MobileToggle onClick={() => setOpen(v => !v)} aria-label="Toggle menu">
          {open ? <AiOutlineClose /> : <AiOutlineMenu />}
        </MobileToggle>
      </Topbar>

      <Sidebar $open={open} aria-label="Sidebar menu">
        <GroupTitle>მენიუ</GroupTitle>
        <Menu>
          <Item to="/" $active={pathname === "/"}>მთავარი</Item>
          <Item to="/add-product" $active={pathname === "/add-product"}>დაამატე პროდუქტი</Item>
          <Item to="/edit-product" $active={pathname === "/edit-product"}>ჩაასწორე პროდუქტი</Item>
          <Item to="/stockmanager" $active={pathname === "/stockmanager"}>ნახე სტოკი</Item>

          <GroupTitle>ბრენდები</GroupTitle>
          <Item to="/AddBrand" $active={pathname === "/AddBrand"}>დაამატე ბრენდი</Item>
          <Item to="/ManageBrands" $active={pathname === "/ManageBrands"}>ჩაასწორე ბრენდი</Item>

          <GroupTitle>ფასდაკლებები / შეკვეთები</GroupTitle>
          <Item to="/AllPerfumesSale" $active={pathname === "/AllPerfumesSale"}>ფასდაკლება</Item>
          <Item to="/all-orders" $active={pathname === "/all-orders"}>შეკვეთები</Item>

          <GroupTitle>კოლექციები და სეტები</GroupTitle>
          <Item to="/add-gift-cards" $active={pathname === "/add-gift-cards"}>სასაჩუქრე ბარათი</Item>
          <Item to="/add-collections" $active={pathname === "/add-collections"}>კოლექციის დამატება</Item>
          <Item to="/add-item-to-collection" $active={pathname === "/add-item-to-collection"}>კოლექციის მართვა</Item>
          <Item to="/gift-sets" $active={pathname === "/gift-sets"}>სასაჩუქრე სეტები</Item>

          <GroupTitle>ლაივი</GroupTitle>
          <Item to="/changes" $active={pathname === "/changes"}>ლაივ ცვლილებები</Item>

          <DangerBtn onClick={handleLogout}>Log out</DangerBtn>
        </Menu>
      </Sidebar>

      {/* მობილურის overlay */}
      <Overlay $open={open} onClick={() => setOpen(false)} />
    </>
  );
}
